import React, { useState, useEffect } from 'react'
import noop from 'lodash/noop'
import { TextInputOld, Button, Dropdown, IDropdownItem } from '@doseme/cohesive-ui'

import { INewSSORealm } from '../../../../types/ssoRealm'
import { TProps } from './types'
import { SSO_REALMS_URL } from '../../../../constants/urls/frontend'
import { FormPageLayout } from '../../../../components/FormPageLayout'
import { StyledForm } from '../../../../components/StyledForm'
import { TFormsValidity } from '../../types'
import { isLoaded } from 'flux-entities'
import { ILabelWithValue } from '../../../../components/SubscriptionsInputModal/types'

type TKey = 'idpHint' | 'realm' | 'installation'

const Create: React.FC<TProps> = ({ ssoRealmsLoading, createSsoRealm, history, installations, fetchInstallations }) => {
  const [newSsoRealm, update] = useState<INewSSORealm>({
    idpHint: '',
    realm: '',
    installation: 0
  })

  const [fieldsValidity, setFieldsValidity] = useState<TFormsValidity<TKey>>({
    idpHint: {
      touched: false,
      valid: false
    },
    realm: {
      touched: false,
      valid: false
    },
    installation: {
      touched: false,
      valid: false
    }
  })

  const [error, updateError] = useState('')

  useEffect(() => {
    if (!installations.ids.length) {
      fetchInstallations()
    }
  }, [installations.ids.length, fetchInstallations])

  if (!isLoaded(installations)) {
    return <div>Loading...</div>
  }

  const handleInput = (name: string, value: string, valid: boolean) => {
    if (valid) {
      update({ ...newSsoRealm, [name]: value })
    }

    setFieldsValidity({
      ...fieldsValidity,
      [name]: {
        touched: true,
        valid
      }
    })
  }

  const handleSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    updateError('')

    const { status, message } = await createSsoRealm({
      ...newSsoRealm
    })

    if (status >= 400) {
      updateError(message)
      return
    }

    history.push(SSO_REALMS_URL)
  }

  const handleInstallationSelect = (item: IDropdownItem) => {
    const numericId = parseInt(item.value, 10)
    update({ ...newSsoRealm, installation: numericId })
    setFieldsValidity({
      ...fieldsValidity,
      installation: {
        touched: true,
        valid: true
      }
    })
  }

  const installationList = installations.ids.reduce<ILabelWithValue[]>((acc, curr) => {
    const installation = installations.all[curr]
    return [...acc, { label: installation.code, value: installation.id.toString() }]
  }, [])

  const formValid = (Object.keys(fieldsValidity) as TKey[]).every(
    (field) => fieldsValidity[field].valid && fieldsValidity[field].touched
  )

  return (
    <FormPageLayout
      title='Create SSO Realm'
      testid='create-sso-realm'
      handleBackClick={() => history.push(SSO_REALMS_URL)}
    >
      <StyledForm handleSubmit={handleSubmit}>
        <TextInputOld
          label='Realm'
          data-test='realm'
          name='realm'
          type='text'
          isRequired
          onBlur={(value, valid) => handleInput('realm', value, valid)}
        />
        <br />

        <TextInputOld
          label='IDP Hint'
          name='idpHint'
          data-test='idp-hint'
          type='text'
          isRequired
          onBlur={(value, valid) => handleInput('idpHint', value, valid)}
        />
        <br />

        <Dropdown
          fieldState='valid'
          showSearchThreshold={0}
          placeholder='Select installation'
          value={newSsoRealm.installation?.toString()}
          data={installationList}
          onSelect={handleInstallationSelect}
        />
        <br />
        <hr />

        <div className='d-flex w-100 justify-content-end'>
          <Button type='submit' variant='primary' onClick={noop} disabled={!formValid} loading={ssoRealmsLoading}>
            Create
          </Button>
        </div>
      </StyledForm>

      {error}
    </FormPageLayout>
  )
}

export { Create }
